import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import {
  LogOut,
  Upload,
  Image,
  Video,
  FileText,
  Trash2,
  Plus,
  ArrowLeft,
  Eye,
  EyeOff,
  Save,
  X,
  Building2,
  Edit2,
  Mail,
  MailOpen
} from 'lucide-react';
import Logo from './Logo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('media');
  const [media, setMedia] = useState([]);
  const [articles, setArticles] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [messages, setMessages] = useState([]);
  const [uploading, setUploading] = useState(false);
  
  // Article form state
  const [showArticleForm, setShowArticleForm] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [articleForm, setArticleForm] = useState({
    title: '',
    content: '',
    excerpt: '',
    featured_image: '',
    published: false
  });
  
  // Sponsor form state
  const [showSponsorForm, setShowSponsorForm] = useState(false);
  const [editingSponsor, setEditingSponsor] = useState(null);
  const [sponsorForm, setSponsorForm] = useState({
    name: '',
    website: ''
  });

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    loadMedia();
    loadArticles();
    loadSponsors();
    loadMessages();
  }, []);

  const loadMedia = async () => {
    try {
      const response = await axios.get(`${API}/media`);
      setMedia(response.data);
    } catch (error) {
      console.error('Error loading media:', error);
    }
  };

  const loadArticles = async () => {
    try {
      const response = await axios.get(`${API}/articles?published_only=false`, { headers });
      setArticles(response.data);
    } catch (error) {
      console.error('Error loading articles:', error);
    }
  };

  const loadSponsors = async () => {
    try {
      const response = await axios.get(`${API}/sponsors`);
      setSponsors(response.data);
    } catch (error) {
      console.error('Error loading sponsors:', error);
    }
  };

  const loadMessages = async () => {
    try {
      const response = await axios.get(`${API}/contact`, { headers });
      setMessages(response.data);
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const handleMarkRead = async (id) => {
    try {
      await axios.put(`${API}/contact/${id}/read`, {}, { headers });
      loadMessages();
    } catch (error) {
      toast.error('Fejl ved opdatering');
    }
  };

  const handleDeleteMessage = async (id) => {
    if (!window.confirm('Er du sikker på du vil slette denne besked?')) return;
    try {
      await axios.delete(`${API}/contact/${id}`, { headers });
      toast.success('Besked slettet!');
      loadMessages();
    } catch (error) {
      toast.error('Fejl ved sletning');
    }
  };

  const unreadCount = messages.filter((m) => !m.is_read).length;

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('title', file.name);

        await axios.post(`${API}/media/upload`, formData, {
          headers: { ...headers, 'Content-Type': 'multipart/form-data' }
        });
      }
      toast.success('Filer uploadet!');
      loadMedia();
    } catch (error) {
      toast.error('Fejl ved upload: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteMedia = async (id) => {
    if (!window.confirm('Er du sikker på du vil slette denne fil?')) return;
    try {
      await axios.delete(`${API}/media/${id}`, { headers });
      toast.success('Fil slettet!');
      loadMedia();
    } catch (error) {
      toast.error('Fejl ved sletning');
    }
  };

  const handleSaveArticle = async () => {
    if (!articleForm.title || !articleForm.content) {
      toast.error('Titel og indhold er påkrævet');
      return;
    }

    try {
      if (editingArticle) {
        await axios.put(`${API}/articles/${editingArticle.id}`, articleForm, { headers });
        toast.success('Artikel opdateret!');
      } else {
        await axios.post(`${API}/articles`, articleForm, { headers });
        toast.success('Artikel oprettet!');
      }
      setShowArticleForm(false);
      setEditingArticle(null);
      setArticleForm({ title: '', content: '', excerpt: '', featured_image: '', published: false });
      loadArticles();
    } catch (error) {
      toast.error('Fejl ved gemning af artikel');
    }
  };

  const handleEditArticle = (article) => {
    setEditingArticle(article);
    setArticleForm({
      title: article.title,
      content: article.content,
      excerpt: article.excerpt || '',
      featured_image: article.featured_image || '',
      published: article.published
    });
    setShowArticleForm(true);
  };

  const handleDeleteArticle = async (id) => {
    if (!window.confirm('Er du sikker på du vil slette denne artikel?')) return;
    try {
      await axios.delete(`${API}/articles/${id}`, { headers });
      toast.success('Artikel slettet!');
      loadArticles();
    } catch (error) {
      toast.error('Fejl ved sletning');
    }
  };

  // Sponsor handlers
  const handleSaveSponsor = async () => {
    if (!sponsorForm.name) {
      toast.error('Navn er påkrævet');
      return;
    }

    try {
      if (editingSponsor) {
        await axios.put(`${API}/sponsors/${editingSponsor.id}`, sponsorForm, { headers });
        toast.success('Sponsor opdateret!');
      } else {
        await axios.post(`${API}/sponsors`, sponsorForm, { headers });
        toast.success('Sponsor tilføjet!');
      }
      setShowSponsorForm(false);
      setEditingSponsor(null);
      setSponsorForm({ name: '', website: '' });
      loadSponsors();
    } catch (error) {
      toast.error('Fejl ved gemning af sponsor');
    }
  };

  const handleEditSponsor = (sponsor) => {
    setEditingSponsor(sponsor);
    setSponsorForm({
      name: sponsor.name,
      website: sponsor.website || ''
    });
    setShowSponsorForm(true);
  };

  const handleDeleteSponsor = async (id) => {
    if (!window.confirm('Er du sikker på du vil slette denne sponsor?')) return;
    try {
      await axios.delete(`${API}/sponsors/${id}`, { headers });
      toast.success('Sponsor slettet!');
      loadSponsors();
    } catch (error) {
      toast.error('Fejl ved sletning');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-[#faf7f2]">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Logo size={40} />
              <div>
                <h1 className="font-bold text-gray-900">Admin Dashboard</h1>
                <p className="text-sm text-gray-500">Velkommen, {user?.username}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                onClick={() => navigate('/')}
                className="gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Se hjemmeside
              </Button>
              <Button
                variant="outline"
                onClick={handleLogout}
                className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <LogOut className="w-4 h-4" />
                Log ud
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="flex flex-wrap gap-4 mb-8">
          <button
            onClick={() => setActiveTab('media')}
            className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all ${
              activeTab === 'media'
                ? 'bg-[#1a472a] text-white shadow-lg'
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Image className="w-5 h-5" />
            Billeder & Videoer
          </button>
          <button
            onClick={() => setActiveTab('articles')}
            className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all ${
              activeTab === 'articles'
                ? 'bg-[#1a472a] text-white shadow-lg'
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            <FileText className="w-5 h-5" />
            Artikler
          </button>
          <button
            onClick={() => setActiveTab('sponsors')}
            className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all ${
              activeTab === 'sponsors'
                ? 'bg-[#1a472a] text-white shadow-lg'
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Building2 className="w-5 h-5" />
            Sponsorer
          </button>
          <button
            onClick={() => setActiveTab('messages')}
            data-testid="admin-tab-messages"
            className={`relative flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all ${
              activeTab === 'messages'
                ? 'bg-[#1a472a] text-white shadow-lg'
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Mail className="w-5 h-5" />
            Beskeder
            {unreadCount > 0 && (
              <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold rounded-full bg-[#c41e3a] text-white">
                {unreadCount}
              </span>
            )}
          </button>
        </div>

        {/* Media Tab */}
        {activeTab === 'media' && (
          <div>
            {/* Upload Area */}
            <div className="bg-white rounded-2xl p-8 mb-8 border-2 border-dashed border-gray-200 hover:border-[#1a472a]/30 transition-colors">
              <input
                type="file"
                id="file-upload"
                multiple
                accept="image/*,video/*"
                onChange={handleFileUpload}
                className="hidden"
              />
              <label
                htmlFor="file-upload"
                className="flex flex-col items-center justify-center cursor-pointer"
              >
                <Upload className="w-12 h-12 text-gray-400 mb-4" />
                <p className="text-lg font-medium text-gray-700 mb-2">
                  {uploading ? 'Uploader...' : 'Klik for at uploade billeder eller videoer'}
                </p>
                <p className="text-sm text-gray-500">
                  JPG, PNG, GIF, WEBP, MP4, WEBM
                </p>
              </label>
            </div>

            {/* Media Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {media.map((item) => (
                <div
                  key={item.id}
                  className="group relative bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow"
                >
                  {item.media_type === 'image' ? (
                    <img
                      src={`${BACKEND_URL}${item.url}`}
                      alt={item.title || item.original_filename}
                      className="w-full h-48 object-cover"
                    />
                  ) : (
                    <div className="relative w-full h-48 bg-gray-900 flex items-center justify-center">
                      <Video className="w-12 h-12 text-white/50" />
                      <video
                        src={`${BACKEND_URL}${item.url}`}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <button
                      onClick={() => handleDeleteMedia(item.id)}
                      className="p-3 bg-red-500 rounded-full text-white hover:bg-red-600 transition-colors"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="p-3">
                    <p className="text-sm text-gray-600 truncate">{item.original_filename}</p>
                    <p className="text-xs text-gray-400 flex items-center gap-1 mt-1">
                      {item.media_type === 'image' ? <Image className="w-3 h-3" /> : <Video className="w-3 h-3" />}
                      {item.media_type === 'image' ? 'Billede' : 'Video'}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {media.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <Image className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p>Ingen billeder eller videoer uploadet endnu</p>
              </div>
            )}
          </div>
        )}

        {/* Articles Tab */}
        {activeTab === 'articles' && (
          <div>
            {!showArticleForm ? (
              <>
                <Button
                  onClick={() => setShowArticleForm(true)}
                  className="mb-8 bg-[#1a472a] hover:bg-[#2d5a3d] gap-2"
                >
                  <Plus className="w-5 h-5" />
                  Opret ny artikel
                </Button>

                {/* Articles List */}
                <div className="space-y-4">
                  {articles.map((article) => (
                    <div
                      key={article.id}
                      className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-xl font-bold text-gray-900">{article.title}</h3>
                            {article.published ? (
                              <span className="flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                                <Eye className="w-3 h-3" /> Publiceret
                              </span>
                            ) : (
                              <span className="flex items-center gap-1 text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                                <EyeOff className="w-3 h-3" /> Kladde
                              </span>
                            )}
                          </div>
                          <p className="text-gray-600 line-clamp-2">{article.excerpt || article.content.substring(0, 150)}...</p>
                          <p className="text-sm text-gray-400 mt-2">
                            Oprettet: {new Date(article.created_at).toLocaleDateString('da-DK')}
                          </p>
                        </div>
                        <div className="flex gap-2 ml-4">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditArticle(article)}
                          >
                            Rediger
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteArticle(article.id)}
                            className="text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {articles.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    <FileText className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p>Ingen artikler oprettet endnu</p>
                  </div>
                )}
              </>
            ) : (
              /* Article Form */
              <div className="bg-white rounded-2xl p-8 shadow-sm">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">
                    {editingArticle ? 'Rediger artikel' : 'Opret ny artikel'}
                  </h2>
                  <button
                    onClick={() => {
                      setShowArticleForm(false);
                      setEditingArticle(null);
                      setArticleForm({ title: '', content: '', excerpt: '', featured_image: '', published: false });
                    }}
                    className="p-2 hover:bg-gray-100 rounded-full"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Titel</label>
                    <Input
                      value={articleForm.title}
                      onChange={(e) => setArticleForm({ ...articleForm, title: e.target.value })}
                      placeholder="Artikelens titel"
                      className="h-12"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Resumé (valgfrit)</label>
                    <Input
                      value={articleForm.excerpt}
                      onChange={(e) => setArticleForm({ ...articleForm, excerpt: e.target.value })}
                      placeholder="Kort beskrivelse af artiklen"
                      className="h-12"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Indhold</label>
                    <Textarea
                      value={articleForm.content}
                      onChange={(e) => setArticleForm({ ...articleForm, content: e.target.value })}
                      placeholder="Skriv artiklens indhold her..."
                      className="min-h-[300px]"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Fremhævet billede URL (valgfrit)</label>
                    <Input
                      value={articleForm.featured_image}
                      onChange={(e) => setArticleForm({ ...articleForm, featured_image: e.target.value })}
                      placeholder="/api/media/files/filnavn.jpg"
                      className="h-12"
                    />
                  </div>

                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="published"
                      checked={articleForm.published}
                      onChange={(e) => setArticleForm({ ...articleForm, published: e.target.checked })}
                      className="w-5 h-5 rounded border-gray-300 text-[#1a472a] focus:ring-[#1a472a]"
                    />
                    <label htmlFor="published" className="text-sm font-medium text-gray-700">
                      Publicer artikel (gør den synlig på hjemmesiden)
                    </label>
                  </div>

                  <div className="flex gap-4 pt-4">
                    <Button
                      onClick={handleSaveArticle}
                      className="bg-[#1a472a] hover:bg-[#2d5a3d] gap-2"
                    >
                      <Save className="w-5 h-5" />
                      {editingArticle ? 'Opdater artikel' : 'Gem artikel'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowArticleForm(false);
                        setEditingArticle(null);
                        setArticleForm({ title: '', content: '', excerpt: '', featured_image: '', published: false });
                      }}
                    >
                      Annuller
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Sponsors Tab */}
        {activeTab === 'sponsors' && (
          <div>
            {!showSponsorForm ? (
              <>
                <Button
                  onClick={() => setShowSponsorForm(true)}
                  className="mb-8 bg-[#1a472a] hover:bg-[#2d5a3d] gap-2"
                >
                  <Plus className="w-5 h-5" />
                  Tilføj ny sponsor
                </Button>

                {/* Sponsors List */}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {sponsors.map((sponsor) => (
                    <div
                      key={sponsor.id}
                      className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-gray-900">{sponsor.name}</h3>
                          {sponsor.website && (
                            <a
                              href={sponsor.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-[#1a472a] hover:underline"
                            >
                              {sponsor.website}
                            </a>
                          )}
                        </div>
                        <div className="flex gap-2 ml-4">
                          <button
                            onClick={() => handleEditSponsor(sponsor)}
                            className="p-2 text-gray-500 hover:text-[#1a472a] hover:bg-gray-100 rounded-full transition-colors"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteSponsor(sponsor.id)}
                            className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {sponsors.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    <Building2 className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p>Ingen sponsorer tilføjet endnu</p>
                  </div>
                )}
              </>
            ) : (
              /* Sponsor Form */
              <div className="bg-white rounded-2xl p-8 shadow-sm max-w-xl">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">
                    {editingSponsor ? 'Rediger sponsor' : 'Tilføj ny sponsor'}
                  </h2>
                  <button
                    onClick={() => {
                      setShowSponsorForm(false);
                      setEditingSponsor(null);
                      setSponsorForm({ name: '', website: '' });
                    }}
                    className="p-2 hover:bg-gray-100 rounded-full"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Sponsor navn *</label>
                    <Input
                      value={sponsorForm.name}
                      onChange={(e) => setSponsorForm({ ...sponsorForm, name: e.target.value })}
                      placeholder="Virksomhedens navn"
                      className="h-12"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Hjemmeside (valgfrit)</label>
                    <Input
                      value={sponsorForm.website}
                      onChange={(e) => setSponsorForm({ ...sponsorForm, website: e.target.value })}
                      placeholder="https://www.eksempel.dk"
                      className="h-12"
                    />
                  </div>

                  <div className="flex gap-4 pt-4">
                    <Button
                      onClick={handleSaveSponsor}
                      className="bg-[#1a472a] hover:bg-[#2d5a3d] gap-2"
                    >
                      <Save className="w-5 h-5" />
                      {editingSponsor ? 'Opdater sponsor' : 'Tilføj sponsor'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowSponsorForm(false);
                        setEditingSponsor(null);
                        setSponsorForm({ name: '', website: '' });
                      }}
                    >
                      Annuller
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        {/* Messages Tab */}
        {activeTab === 'messages' && (
          <div data-testid="admin-messages-panel">
            <div className="space-y-4">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  data-testid={`message-card-${msg.id}`}
                  className={`bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow border-l-4 ${
                    msg.is_read ? 'border-gray-200' : 'border-[#c41e3a]'
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1 flex-wrap">
                        <h3 className="text-lg font-bold text-gray-900">{msg.name}</h3>
                        {!msg.is_read && (
                          <span className="text-xs bg-[#c41e3a]/10 text-[#c41e3a] px-2 py-0.5 rounded-full font-medium">
                            Ny
                          </span>
                        )}
                      </div>
                      <a href={`mailto:${msg.email}`} className="text-sm text-[#1a472a] hover:underline">
                        {msg.email}
                      </a>
                      <p className="text-gray-700 mt-3 whitespace-pre-wrap">{msg.message}</p>
                      <p className="text-xs text-gray-400 mt-3">
                        {new Date(msg.created_at).toLocaleString('da-DK')}
                        {' · '}
                        {msg.email_sent ? 'E-mail sendt' : 'Kun gemt i backoffice'}
                      </p>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      {!msg.is_read && (
                        <button
                          onClick={() => handleMarkRead(msg.id)}
                          title="Markér som læst"
                          data-testid={`message-read-${msg.id}`}
                          className="p-2 text-gray-500 hover:text-[#1a472a] hover:bg-gray-100 rounded-full transition-colors"
                        >
                          <MailOpen className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteMessage(msg.id)}
                        title="Slet besked"
                        data-testid={`message-delete-${msg.id}`}
                        className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {messages.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <Mail className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p>Ingen beskeder modtaget endnu</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
